import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BookOpen, Download } from 'lucide-react';
import { api, mediaUrl } from '../services/api';
import CatalogFlipbookModal from '../components/catalog/CatalogFlipbookModal';
import useBodyScrollLock from '../hooks/useBodyScrollLock';

export default function ProductCatalogDetailPage() {
  const { id } = useParams();
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(false);

  useBodyScrollLock(active);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.getProductCatalog(id)
      .then((res) => {
        if (mounted) setCatalog(res?.data || null);
      })
      .catch(() => mounted && setCatalog(null))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [id]);

  const pdfUrl = useMemo(() => (catalog?.id ? api.getProductCatalogFileUrl(catalog.id) : ''), [catalog]);
  const downloadUrl = useMemo(() => (catalog?.id ? api.getProductCatalogDownloadUrl(catalog.id) : ''), [catalog]);

  return (
    <main className="min-h-[70vh] bg-gray-50" dir="rtl">
      <section className="border-b border-gray-200 bg-white">
        <div className="pp-container py-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-[#005bbb]">
            <BookOpen className="h-4 w-4" />
            کاتالوگ محصولات
          </div>
          <h1 className="mt-4 text-2xl font-extrabold text-slate-900 sm:text-3xl">
            {loading ? 'در حال بارگذاری...' : catalog?.title || 'کاتالوگ پیدا نشد'}
          </h1>
        </div>
      </section>

      <section className="pp-container py-10">
        {loading ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-500">در حال بارگذاری...</div>
        ) : catalog ? (
          <div className="grid gap-8 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm lg:grid-cols-[360px_minmax(0,1fr)] lg:p-8">
            <div className="grid min-h-64 place-items-center rounded-2xl border border-gray-100 bg-slate-50 p-8">
              {catalog.logo_url ? (
                <img src={mediaUrl(catalog.logo_url)} alt={catalog.title} className="max-h-52 w-full object-contain" />
              ) : (
                <span className="grid h-28 w-28 place-items-center rounded-2xl bg-blue-50 text-[#005bbb]">
                  <BookOpen className="h-12 w-12" />
                </span>
              )}
            </div>
            <div className="flex flex-col justify-center">
              <h2 className="text-xl font-black text-slate-900 sm:text-2xl">{catalog.title}</h2>
              <p className="mt-4 max-w-3xl text-sm leading-8 text-slate-600">
                {catalog.description || 'توضیحی برای این کاتالوگ ثبت نشده است.'}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setActive(true)}
                  className="inline-flex items-center justify-center rounded-2xl bg-[#005bbb] px-6 py-3 text-sm font-extrabold text-white transition hover:bg-[#004a99]"
                >
                  نمایش کاتالوگ
                </button>
                <a
                  href={downloadUrl}
                  className="inline-flex items-center gap-2 rounded-2xl border border-blue-100 bg-blue-50 px-5 py-3 text-sm font-extrabold text-[#005bbb] transition hover:bg-blue-100"
                >
                  <Download className="h-4 w-4" />
                  دانلود PDF
                </a>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-500">کاتالوگ موردنظر پیدا نشد.</div>
        )}
      </section>

      {active && catalog && (
        <CatalogFlipbookModal catalog={catalog} pdfUrl={pdfUrl} downloadUrl={downloadUrl} onClose={() => setActive(false)} />
      )}
    </main>
  );
}
